// La escalera de la portada: de qué color es el fondo a cada altura del rollo de papel.
//
//     node scripts/prueba_escalera.mjs        (desde frontend/)
//
// QUÉ SUSTITUYE, Y POR QUÉ. La portada bajaba de negro a blanco con tres franjas de degradado
// puestas entre sección y sección. Funcionaba, pero el cambio ocurría SIEMPRE EN EL MISMO SITIO
// del documento: si la franja te queda a media pantalla y paras de bajar, se te queda media
// pantalla negra y media blanca, con la costura a la vista. Gabri, 8 de octubre de 2026: *«en
// lugar de una franja de degradado fija en el medio, quiero que el fondo de la página haga una
// transición de color fluida mediante scroll»*.
//
// LO QUE CAMBIA ES QUIÉN MANDA: ya no manda el documento, manda **dónde estás mirando**. El
// fondo es uno solo, fijo detrás de todo, y su color se calcula con la posición de las
// secciones respecto a la ventana. No hay costura porque no hay dos cosas que coser.
//
// AQUÍ NO HAY NI REACT NI NAVEGADOR: entran números —dónde empieza cada sección, cuánto se ha
// bajado, cuánto mide la ventana— y sale un color. Por eso se puede probar sin abrir nada, que
// es lo que esta casa hace con todo lo que tiene cuentas dentro.

/**
 * LOS SIETE ESCALONES, DEL MÁS OSCURO AL MÁS CLARO, Y LOS SIETE SON GRISES.
 *
 * SON SIETE PARA QUE NO SE REPITAN. Lo pidió Gabri el 9 de octubre de 2026: *«que no haya dos
 * secciones seguidas con el mismo tono de fondo»*. Con cinco, el vídeo y los testimonios caían
 * los dos en el mismo tono y se leían como una sola sección larguísima. Dos secciones seguidas
 * del mismo color no son dos secciones: la frontera entre ellas desaparece, y con ella la
 * sensación de estar avanzando por algo.
 *
 * Y SON GRISES DESDE EL 18 DE OCTUBRE DE 2026, mirando las cuatro escaleras una al lado de
 * otra. Eran cálidas: el mismo 9 de octubre Gabri dijo *«me encanta este tono rojizo, suave»*,
 * y los siete salieron con el rojo hasta catorce puntos por encima del azul —`#EDE1DF`,
 * `#F4EBE9`—. **Suave de cerca es rosa en pantalla completa**: nueve días después, viendo la
 * portada entera, *«los colores han quedado muy rositas y pastel»*. Lo que se vio al comparar
 * es que no era un tono mal elegido, era la escala: un matiz que no se nota en un botón ocupa
 * media pantalla aquí. Los dos colores de la casa, `#0B0B0B` y `#F5F5F5`, son neutros.
 *
 * Y LOS CUATRO CLAROS SE SEPARAN DE VERDAD, que era la otra mitad de «pastel». Estaban los
 * cuatro entre el 225 y el 251: al bajar no cambiaba nada, y una escalera que no escalona es
 * un fondo plano con pasos de más. Ahora van del 217 al blanco.
 *
 * LO QUE NO CAMBIA: NINGÚN FONDO LLEGA AL NEGRO DE LA CASA. Lo pidió Gabri el 9 de octubre
 * —*«evitamos el negro absoluto, intenta bajar el negro de la landing»*— y sigue valiendo: un
 * negro de pantalla completa detrás de media portada es una pared, y encima apaga el rojo que
 * lleva al lado. `#0B0B0B` se queda donde estaba —textos, botones y el mundo—, y el escalón
 * más oscuro de aquí empieza un punto por encima. `prueba_portada.mjs` lo vigila, y fue ese
 * portero el que paró la primera versión de esta escalera, que arrancaba en `#0B0B0B` clavado.
 */
// LOS NOMBRES DICEN EL COLOR, y dos dejaron de decirlo al quitar el cálido: `brasa` era un
// marrón y `rubor`, un rosa. Un nombre que miente es peor que uno feo —el día que alguien
// busque «el tono rubor» va a buscar rosa—, así que pasaron a `hollin` y `ceniza`. El
// `data-tono` de las secciones va con ellos, y `prueba_portada.mjs` no deja atrás ninguno:
// «y todos son de la escalera» se pone en rojo con el primero que se olvide.
export const TONOS = {
  tinta: "#101010",
  carbon: "#181818",
  hollin: "#222222",
  piedra: "#D9D9D9",
  ceniza: "#E8E8E8",
  nacar: "#F3F3F3",
  blanco: "#FFFFFF",
};

/** Del más oscuro al más claro. El orden es el de la luz, no el del gusto. */
export const ESCALERA = ["tinta", "carbon", "hollin", "piedra", "ceniza", "nacar", "blanco"];

/**
 * DÓNDE OCURRE EL CAMBIO, EN FRACCIÓN DE PANTALLA. La transición de una sección empieza cuando
 * su borde de arriba sube por encima del 72 % de la ventana y termina al llegar al 28 %.
 *
 * NO SON NÚMEROS BONITOS: son los que dejan el cambio **en el aire**. El texto de la sección que
 * se va está por encima de su borde y el de la que entra, por debajo; mientras el borde cruza el
 * 44 % central, lo que ocupa esa banda es el hueco entre las dos —el `pt` de la que entra y el
 * `pb` de la que sale—. Un cambio de fondo que pilla un párrafo a medias le baja el contraste
 * justo mientras se lee, y eso no se nota mirando: se nota leyendo.
 */
export const EMPIEZA = 0.72;
export const ACABA = 0.28;

const aRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const aHex = (rgb) => "#" + rgb.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");

/** El color a medio camino entre dos, con `t` de 0 a 1. Se mezcla en RGB, que para dos grises
 *  de la misma familia —y todos éstos lo son— no tiene ningún paso raro por el medio. */
export function mezcla(a, b, t) {
  // LOS EXTREMOS SE DEVUELVEN TAL CUAL, y no es un atajo: pasados por RGB y de vuelta, `#0B0B0B`
  // sale `#0b0b0b` —el mismo color escrito de otra forma—, y entonces «¿el fondo es ya el tono
  // de esta sección?» se contesta que no comparando dos cadenas iguales. Un color que no se
  // reconoce a sí mismo rompe todo lo que decide mirándolo.
  if (t <= 0) return a;
  if (t >= 1) return b;
  const [x, y] = [aRgb(a), aRgb(b)];
  return aHex(x.map((v, i) => v + (y[i] - v) * t));
}

/**
 * Cuánto ha avanzado la entrada de una sección cuyo borde está a `arriba` px del alto de la
 * ventana. 0 mientras viene de abajo, 1 cuando ya ha pasado.
 *
 * `queda` son los píxeles de rollo que aún se pueden bajar, y está aquí por **el final de la
 * página**: la última sección no llega nunca al 28 % de la ventana, porque se acaba el papel
 * antes. Sin esto, la portada se queda abajo del todo en un gris a medio camino que ya no se
 * puede terminar de bajar —y el espejo lo enseñó a la primera—.
 *
 * LO QUE HACE ES ADELANTAR LA TRANSICIÓN JUSTO LO QUE NO SE VA A PODER BAJAR, que es una línea:
 * si a la sección le falta más de lo que queda, se la trata como si estuviera `queda` píxeles
 * por encima del final. Con papel de sobra no cambia nada; al llegar al fondo, el color llega
 * entero. No es un caso raro: es **el último tramo de todas las páginas**.
 */
export function avance(arriba, ventana, queda = Infinity) {
  if (!ventana) return 0;
  const desde = ventana * EMPIEZA;
  const hasta = ventana * ACABA;
  const efectivo = Math.min(arriba, hasta + Math.max(0, queda));
  return Math.min(1, Math.max(0, (desde - efectivo) / (desde - hasta)));
}

/**
 * EL COLOR DEL FONDO AHORA MISMO.
 *
 * `tramos` son las secciones en el orden en que están en la página: `{ tono, arriba }`, con
 * `arriba` en píxeles **desde el borde de arriba de la ventana** (lo que devuelve
 * `getBoundingClientRect().top`). Devuelve el hex del fondo.
 *
 * CÓMO SE RESUELVE, Y ES UNA SOLA REGLA: se empieza por el tono del primer tramo y se va
 * mezclando con cada uno de los siguientes según su avance. Una sección a medio entrar deja el
 * fondo a medio camino; una que ya pasó lo deja entero en el suyo. Así **nunca hay dos verdades
 * a la vez**, que es lo que pasaría teniendo cada sección su propio fondo y encima una franja.
 */
export function colorDelFondo(tramos, ventana, queda = Infinity) {
  if (!tramos || !tramos.length) return TONOS[ESCALERA[0]];
  let color = TONOS[tramos[0].tono] || TONOS[ESCALERA[0]];
  for (const t of tramos.slice(1)) {
    const suyo = TONOS[t.tono];
    if (!suyo) continue;
    color = mezcla(color, suyo, avance(t.arriba, ventana, queda));
  }
  return color;
}

/**
 * CUÁNTO SE LEE EL CONTENIDO DE UNA SECCIÓN SOBRE EL FONDO QUE HAY AHORA.
 *
 * Una sección escribe su texto para SU tono: la cinta lo escribe blanco porque es negra, y la
 * metodología negro porque es clara. Mientras el fondo pasa de uno a otro, el texto de la que
 * se va queda sobre un fondo que no es el suyo, y ahí es donde se pierde el contraste. Esto
 * devuelve lo que vale ese contenido ahora mismo: 1 si el fondo sigue siendo el suyo, y hacia 0
 * a medida que se aleja.
 *
 * NO ES UN EFECTO BONITO, ES LA REGLA DEL CONTRASTE: lo que no se puede leer se va, y lo que
 * entra aparece cuando ya hay fondo donde leerlo. Si el texto se quedara entero, habría medio
 * segundo de gris sobre gris en cada transición — y medio segundo es lo que tarda alguien en
 * decidir que una página está mal hecha.
 *
 * SE MIDE EN LUZ Y NO EN COLOR porque es la luz lo que hace legible un texto. La ventana es de
 * 0,45: por debajo de esa diferencia de luminancia el texto aún se defiende, y por encima ya no.
 */
export const HOLGURA = 0.12;

/**
 * La luz de un color, de 0 a 1. Con los coeficientes de siempre —el ojo ve mucho más el verde
 * que el azul—, no con la media de los tres canales.
 *
 * ESTÁ EXPORTADA PORQUE LA ESCALERA SE MIDE EN LUZ Y NO EN POSICIONES. Antes, «esta sección da
 * un salto respecto a la de arriba» se contestaba restando índices de una lista, y eso se rompe
 * en cuanto la lista crece: añadir dos escalones oscuros convirtió saltos de siempre en saltos
 * de tres. La luz no se mueve cuando la lista cambia.
 */
export function luz(hex) {
  const [r, g, b] = aRgb(hex);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

export function legibilidad(fondo, tono) {
  if (!tono) return 1;
  // LA HOLGURA ES LO QUE IMPIDE QUE MEDIA PORTADA PARPADEE. Entre `piedra` y `off` hay quince
  // puntos de luz sobre doscientos cincuenta y cinco: son dos escalones vecinos a propósito, y
  // un texto negro se lee igual de bien sobre los dos. Sin esta zona muerta, bajar de una
  // sección clara a la siguiente atenuaría las dos un catorce por ciento sin ningún motivo.
  const dif = Math.max(0, Math.abs(luz(fondo) - luz(tono)) - HOLGURA);
  return Math.min(1, Math.max(0, 1 - dif / 0.45));
}
